/**
 * Build FrameRenderer props from a DesignStudioItem.
 *
 * Mirrors the computed properties in DesignStudioItemForm.vue (lines 822–1051),
 * but works from a flat chosen_options array. A fetched availableOptions array can
 * still be supplied as a fallback for non-rehydrated items.
 * rather than a live reactive selection map.
 *
 * Usage:
 *   const opts = await fetchAvailableOptions(item.config_data.substrate)
 *   const props = buildRendererPropsFromItem(item, opts)
 *   // spread props onto a hidden <FrameRenderer v-bind="props" />
 */

import { getSrc } from '@/services/media.js'
import { resolveSubstrateBackData } from '@/utils/substrates.js'

function getOptionLabel(opt) {
  const val = opt.value
  if (val === null || val === undefined || val === '') return opt.name || ''
  if (typeof val === 'number') return String(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  return String(val)
}

function isAcousticTwoSidedChangeable(effectiveOptions, availableOptions, chosenOptions = []) {
  for (const [cat, optId] of Object.entries(effectiveOptions)) {
    if (!optId) continue
    const opt = availableOptions.find(o => o.id === optId)
      || chosenOptions.find(o => o.name === cat && (o.option_id === optId || String(o.option_id) === String(optId)))
    if (!opt) continue
    const raw = String(opt.value != null ? opt.value : opt.name || '').toLowerCase()
    const c = String(cat || '').toLowerCase()
    if (/2\s*[-\s]*sided\s*changeable/i.test(raw) || /2\s*[-\s]*sided\s*changeable/i.test(c)) return true
  }
  return false
}

function parseInches(val) {
  if (val == null) return null
  const s = String(val).replace(/["']/g, '').trim()
  const frac = s.match(/^(\d+)\s*\/\s*(\d+)/)
  if (frac) return Number(frac[1]) / Number(frac[2])
  const parsed = parseFloat(s)
  return isNaN(parsed) ? null : parsed
}

export function buildRendererPropsFromItem(item, availableOptions = []) {
  const cfg = item.config_data || {}
  const chosenOptions = cfg.chosen_options || []

  // { categoryName: optionId }
  const effectiveOptions = {}
  chosenOptions.forEach(opt => {
    if (opt.option_id && opt.name) effectiveOptions[opt.name] = opt.option_id
  })

  const findChosenOpt = (category, optId) => chosenOptions.find(o =>
    o.name === category && (o.option_id === optId || String(o.option_id) === String(optId))
  )

  const findOpt = (pattern) => {
    for (const [cat, optId] of Object.entries(effectiveOptions)) {
      if (!optId || !pattern.test(cat)) continue
      const found = availableOptions.find(o => o.id === optId)
      if (found) return found
      const chosen = findChosenOpt(cat, optId)
      if (chosen) return chosen
    }
    return null
  }

  const getOptionValue = (pattern) => {
    const opt = findOpt(pattern)
    if (!opt) return null
    return opt.value != null ? String(opt.value) : (opt.name || null)
  }

  // --- Frame ---
  const frameOpt = findOpt(/frame/i)
  const frameData = frameOpt?.extra_data?.top_image ? frameOpt.extra_data : null

  // --- Mats ---
  const matDataArray = []
  for (const n of [1, 2, 3]) {
    const opt = findOpt(new RegExp(`^Mat\\s*${n}$`, 'i'))
    if (opt?.extra_data?.color) {
      matDataArray.push({
        color: opt.extra_data.color,
        depth: 0.0625,
        coreColor: opt.extra_data.color,
      })
    }
  }

  // Per-mat widths — mirrors computedMatWidthsArray (DesignStudioItemForm:884–908)
  let matWidthsArray = null
  let matWidth = { top: 0, bottom: 0, left: 0, right: 0 }
  if (matDataArray.length) {
    const total = { top: 2, bottom: 2, left: 2, right: 2 }
    for (const [cat, optId] of Object.entries(effectiveOptions)) {
      if (!optId || !/mat\s*width/i.test(cat)) continue
      const opt = availableOptions.find(o => o.id === optId) || findChosenOpt(cat, optId)
      if (!opt) continue
      const val = parseFloat(opt.value)
      if (isNaN(val)) continue
      const lower = cat.toLowerCase()
      if (lower.includes('top')) total.top = val
      else if (lower.includes('bottom')) total.bottom = val
      else if (lower.includes('left')) total.left = val
      else if (lower.includes('right')) total.right = val
      else total.top = total.bottom = total.left = total.right = val
    }
    matWidthsArray = matDataArray.map((_, i) =>
      i === 0
        ? { ...total }
        : { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 }
    )
    matWidth = matWidthsArray.length === 1
      ? matWidthsArray[0]
      : matWidthsArray.reduce((a, w) => ({
          top: a.top + w.top,
          bottom: a.bottom + w.bottom,
          left: a.left + w.left,
          right: a.right + w.right,
        }), { top: 0, bottom: 0, left: 0, right: 0 })
  }

  // --- Canvas mount ---
  const canvasMountOpt = findOpt(/canvas\s*mount/i)
  const canvasMount = canvasMountOpt
    ? (canvasMountOpt.value != null ? String(canvasMountOpt.value) : (canvasMountOpt.name || ''))
    : ''

  // --- Float frame ---
  const floatFrameOpt = findOpt(/float\s*frame/i)
  const floatFrameData = floatFrameOpt?.extra_data ?? null

  // --- Stretcher bars ---
  const stretcherOpt = findOpt(/stretcher/i)
  const stretcherDepth = stretcherOpt
    ? (parseInches(stretcherOpt.value != null ? String(stretcherOpt.value) : stretcherOpt.name) ?? 0)
    : 0

  // --- Hang hardware ---
  const hangOpt = findOpt(/hang/i)
  const hangData = hangOpt ? { name: getOptionLabel(hangOpt) } : null

  // --- Substrate back data ---
  const substrateBackData = resolveSubstrateBackData(cfg.substrate, getOptionValue)

  // --- Artwork filter (mirrors computedArtworkFilter:1026–1051) ---
  let artworkFilter = null
  let grayscale = false
  let sepia = false
  for (const [cat, optId] of Object.entries(effectiveOptions)) {
    if (!optId) continue
    const opt = availableOptions.find(o => o.id === optId) || findChosenOpt(cat, optId)
    if (!opt) continue
    const rawValue = opt.value != null ? opt.value : opt.name
    const normalizedValue = String(rawValue || '').trim().toLowerCase()
    const isEnabled = ['yes', 'true', '1', 'on'].includes(normalizedValue)
    const normalizedCat = String(cat || '').trim().toLowerCase()
    if (/black\s*(and|&)\s*white/.test(normalizedCat)) grayscale = isEnabled
    if (/sepia/.test(normalizedCat)) sepia = isEnabled
  }
  if (grayscale || sepia) artworkFilter = { grayscale, sepia }

  const acousticTwoSidedChangeable = /acoustic/i.test(cfg.substrate || '')
    && isAcousticTwoSidedChangeable(effectiveOptions, availableOptions, chosenOptions)

  return {
    substrate: cfg.substrate || '',
    artWidth: parseFloat(cfg.width) || 16,
    artHeight: parseFloat(cfg.height) || 20,
    artworkImageUrl: getSrc(item.imagedata, 'imgsrc') || getSrc(item.imagedata) || null,
    artworkScaleX: cfg.artwork_scale_x ?? 1,
    artworkScaleY: cfg.artwork_scale_y ?? cfg.artwork_scale_x ?? 1,
    artworkRotation: cfg.artwork_rotation ?? 0,
    artworkOffsetLeft: cfg.artwork_offset_left ?? 0,
    artworkOffsetTop: cfg.artwork_offset_top ?? 0,
    frameData,
    matData: matDataArray[0] || null,
    matDataArray: matDataArray.length ? matDataArray : null,
    matWidth,
    matWidthsArray,
    hangData,
    stretcherDepth,
    canvasMount,
    floatFrameData,
    substrateBackData,
    artworkFilter,
    acousticTwoSidedChangeable,
  }
}
